import { useState } from 'react';
import type { OrderPreference } from '../domain/types';
import { getServicesPrice } from '../store/orderStore';

type ServicesSheetProps = {
  available: OrderPreference[];
  selected: string[];
  currency: string;
  onApply: (ids: string[]) => void;
  onClose: () => void;
};

// Ukrainian labels for known preference keys, mirroring flutter_client
// OrderPreferenceOption.title (pet, courier_delivery, jump_start, child_seat,
// air_conditioning). Falls back to the backend-provided name.
const KEY_LABELS: Record<string, string> = {
  animaltransportation: 'Перевезення тварин',
  pet: 'Перевезення тварин',
  courierdelivery: 'Курʼєрська доставка',
  courier: 'Курʼєрська доставка',
  jumpstart: 'Прикурити акумулятор',
  childseat: 'Дитяче крісло',
  airconditioning: 'Кондиціонер',
};

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function preferenceLabel(pref: OrderPreference): string {
  const localized = KEY_LABELS[normalizeKey(pref.key)];
  return localized ?? pref.name ?? pref.key;
}

export function ServicesSheet({ available, selected, currency, onApply, onClose }: ServicesSheetProps) {
  const [draft, setDraft] = useState<string[]>(selected);

  const toggle = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const total = getServicesPrice(available, draft);

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="services-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grabber" />
        <h2 className="sheet-title">Послуги</h2>

        {available.length === 0 ? (
          <p className="muted">Послуги зараз недоступні.</p>
        ) : (
          <ul className="services-list">
            {available.map((pref) => {
              const checked = draft.includes(pref.id);
              return (
                <li
                  key={pref.id}
                  className={`service-item${checked ? ' checked' : ''}`}
                  onClick={() => toggle(pref.id)}
                >
                  <span className="service-check" aria-hidden>{checked ? '✓' : ''}</span>
                  <span className="service-name">{preferenceLabel(pref)}</span>
                  <span className="service-price">+{Math.round(pref.price)} {currency}</span>
                </li>
              );
            })}
          </ul>
        )}

        <div className="services-total">
          <span>Сума послуг</span>
          <strong>+{total} {currency}</strong>
        </div>

        <div className="sheet-actions">
          <button className="link-button" onClick={onClose}>Скасувати</button>
          <button className="primary-button" onClick={() => onApply(draft)}>Застосувати</button>
        </div>
      </div>
    </div>
  );
}
